import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { Edit, MessageSquare, AlertCircle, TrendingUp, CheckCircle, Clock, CreditCard, Percent, Wallet, Download, ChevronRight, FileText, Building2, Shield, Lock, AlertTriangle, Zap, MessageCircle, FileCheck, Plus, X } from 'lucide-react';

const AdminCustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // (audit 2026-06-10) — internal admin notes textarea + upload button
  // were not wired. New state below + the SAVE NOTE handler down below now
  // POST to /admin/users/:id/notes (audit_log entry) and the UPLOAD NEW
  // button opens a hidden file input that POSTs to /admin/users/:id/documents.
  const [internalNoteText, setInternalNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const handleSaveInternalNote = async () => {
    if (!internalNoteText.trim()) { alert('Note is empty'); return; }
    setSavingNote(true);
    try {
      const res: any = await apiService.request(`/admin/users/${customerId}/notes`, 'POST', {
        note: internalNoteText,
      });
      if (res?.success) {
        alert('Note saved');
        setInternalNoteText('');
      } else {
        alert('Error: ' + (res?.message || 'Failed to save note'));
      }
    } catch (e: any) {
      alert('Failed: ' + (e?.message || 'Network error'));
    } finally {
      setSavingNote(false);
    }
  };
  const handleUploadDocument = async (file: File) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res: any = await apiService.request(`/admin/users/${customerId}/documents`, 'POST', fd);
      if (res?.success) {
        alert('Document uploaded');
        window.location.reload();
      } else {
        alert('Error: ' + (res?.message || 'Upload failed'));
      }
    } catch (e: any) {
      alert('Failed: ' + (e?.message || 'Network error'));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiService.request(`/admin/customers/${customerId}`, 'GET');
        if (response.success && response.data) {
          setFetchedData(Array.isArray(response.data) ? response.data[0] : response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  const customer = fetchedData || {};
  const customerName = customer.full_name || customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Customer';
  const customerInitials = customerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || 'C';
  const customerId2 = customer.id || customerId;

  const tabs = [
    { id: 'overview', label: 'Overview' },
  ];

  const overviewOrders = (customer.recent_orders || customer.orders || []).slice(0, 3).map((o: any) => ({
    id: o.booking_reference || o.id ? `ORD-${o.id}` : '—',
    description: o.service_name || o.description || '—',
    amount: o.total_amount_pence ? `£${(o.total_amount_pence / 100).toFixed(2)}` : o.total ? `£${Number(o.total).toFixed(2)}` : '—',
    status: o.status || 'Pending',
    statusColor: o.status === 'completed' ? 'text-green-600' : 'text-blue-600'
  }));

  const recentOrders = (customer.orders || customer.bookings || []).map((o: any) => ({
    id: o.booking_reference || `ORD-${o.id}`,
    asset: o.property_address || o.service_name || '—',
    amount: o.total_amount_pence ? `£${(o.total_amount_pence / 100).toFixed(2)}` : '—',
    date: o.created_at ? new Date(o.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
    status: (o.status || 'PENDING').toUpperCase(),
    lead: o.provider_name || 'System',
    icon: '📋'
  }));

  const properties = (customer.properties || []).map((p: any, i: number) => ({
    id: p.id || i + 1,
    name: p.property_name || p.address || '—',
    type: p.property_type || '—',
    value: '—',
    image: ['bg-gradient-to-br from-blue-400 to-blue-600', 'bg-gradient-to-br from-amber-400 to-amber-600', 'bg-gradient-to-br from-teal-400 to-teal-600'][i % 3]
  }));

  const auditLogs = (customer.audit_log || customer.activity_log || []).map((a: any) => ({
    id: a.id,
    timestamp: a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
    time: a.created_at ? new Date(a.created_at).toLocaleTimeString('en-GB') + ' UTC' : '—',
    actor: a.actor_type || 'System',
    actorName: a.actor_name || 'System',
    actorInitial: (a.actor_name || 'SY').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
    category: (a.category || 'SYSTEM').toUpperCase(),
    categoryColor: 'bg-gray-200 text-gray-700',
    description: a.description || a.action || '—',
    ip: a.ip_address || '—'
  }));

  const documents = (customer.documents || []).map((d: any, i: number) => ({
    id: d.id || i + 1,
    name: d.document_name || d.file_name || '—',
    type: d.document_type || d.category || '—',
    dateUploaded: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
    status: (d.status || 'PENDING').toUpperCase(),
    statusColor: d.status === 'approved' ? 'bg-green-100 text-green-700' : d.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700',
    icon: '📄'
  }));

  const internalNotes = (customer.notes || customer.internal_notes || []).map((n: any) => ({
    id: n.id,
    author: n.author_name || n.created_by || 'Admin',
    avatar: (n.author_name || 'AD').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
    timestamp: n.created_at ? new Date(n.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }).toUpperCase() : '—',
    content: n.content || n.note || '—',
    avatarBg: 'bg-gradient-to-br from-teal-400 to-teal-600'
  }));

  const invoices = (customer.invoices || []).map((inv: any) => {
    const status = (inv.status || 'PENDING').toUpperCase();
    return {
      id: `#INV-${inv.id}`,
      issuedDate: inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
      property: inv.property_address || inv.service_name || '—',
      amount: inv.total_amount_pence ? `£${(inv.total_amount_pence / 100).toFixed(2)}` : inv.amount ? `£${Number(inv.amount).toFixed(2)}` : '—',
      status,
      statusColor: status === 'PAID' ? 'text-green-600 bg-green-50' : status === 'OVERDUE' ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50'
    };
  });

  const quotes = (customer.quotes || customer.service_requests || []).map((q: any) => {
    const status = (q.status || q.approval_status || 'PENDING').toUpperCase();
    return {
      id: `QT-${q.id}`,
      serviceType: q.service_name || q.service_type || '—',
      property: q.property_address || q.postcode || '—',
      status,
      statusColor: status === 'APPROVED' ? 'bg-green-100 text-green-700' : status === 'UNDER REVIEW' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700',
      statusDot: status === 'APPROVED' ? 'bg-green-600' : status === 'UNDER REVIEW' ? 'bg-blue-600' : 'bg-gray-600',
      amount: q.total_amount_pence ? `£${(q.total_amount_pence / 100).toFixed(2)}` : q.quoted_price ? `£${Number(q.quoted_price).toFixed(2)}` : '—'
    };
  });

  const [currentInvoicePage, setCurrentInvoicePage] = useState(1);
  const [currentQuotePage, setCurrentQuotePage] = useState(1);

  // Handler: Update customer profile
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', phone: '', company_name: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);

  const openEditModal = () => {
    setEditForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company_name: customer.company_name || customer.company || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateCustomerProfile = async (customerData: any) => {
    setSavingCustomer(true);
    try {
      const response = await apiService.request(`/admin/customers/${customerId}/update`, 'PUT', {
        ...customerData,
        updatedBy: localStorage.getItem('adminId'),
      });
      if (response.success) {
        alert('✓ Customer updated');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  // Handler: Suspend customer account
  const handleSuspendCustomer = async () => {
    if (!window.confirm('Suspend this customer account?')) return;
    try {
      const response = await apiService.request(`/admin/customers/${customerId}/suspend`, 'POST', {
        suspendedBy: localStorage.getItem('adminId'),
        suspendedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Account suspended');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Send message to customer
  const handleSendMessage = async (message: string) => {
    try {
      const response = await apiService.request(`/admin/customers/${customerId}/send-message`, 'POST', {
        message: message,
        sentBy: localStorage.getItem('adminId'),
        sentAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Message sent');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export customer data
  const handleExportCustomerData = async () => {
    try {
      const response = await apiService.request(`/admin/customers/${customerId}/export`, 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customer-${customerId}-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Data exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Customer Details - Virewire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8 shadow-sm">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-6">
                {/* Avatar */}
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {customerInitials}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{customerName}</h1>
                  </div>
                  {customer.company && <p className="text-sm text-gray-600 mb-1">{customer.company}</p>}
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span>🆔 ID: #{customerId2}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate(`/admin/support-disputes?customerId=${customerId || ''}`)}
                  className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold py-2 px-4 flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Message
                </Button>
                <Button
                  onClick={openEditModal}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Account
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center border-b border-gray-200 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Content */}
          {activeTab === 'overview' && (
            <>
              {/* Metrics */}
              <div className="grid grid-cols-4 gap-6 mb-8">
                {/* Total Balance */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Total Balance</p>
                  <p className="text-3xl font-bold text-gray-900 mb-2">
                    {customer.total_balance != null ? `£${Number(customer.total_balance).toFixed(2)}` : '—'}
                  </p>
                  {customer.overdue_amount > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-red-600 font-bold flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Overdue: £{Number(customer.overdue_amount).toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Lifetime Value */}
                <div className="bg-green-600 text-white rounded-xl p-6 shadow-lg">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3 opacity-90">Lifetime Value</p>
                  <p className="text-3xl font-bold mb-2">
                    {customer.lifetime_value != null ? `£${Number(customer.lifetime_value).toFixed(2)}` : '—'}
                  </p>
                  <p className="text-sm flex items-center gap-1 opacity-90">
                    <TrendingUp className="w-4 h-4" />
                    Total spend to date
                  </p>
                </div>

                {/* Active Orders */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Active Orders</p>
                  <p className="text-3xl font-bold text-gray-900 mb-2">
                    {customer.active_orders != null ? customer.active_orders : recentOrders.filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED').length || 0}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Active bookings
                  </p>
                </div>

              </div>

              {/* Contact & Billing */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                {/* Contact Details */}
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Contact Details</h2>
                    <button
                      onClick={openEditModal}
                      className="text-teal-600 hover:text-teal-900 font-bold text-sm"
                    >
                      Edit
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Email Address</p>
                      <p className="text-gray-900 font-medium">{customer.email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Phone Number</p>
                      <p className="text-gray-900 font-medium">{customer.phone || customer.contact_phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Primary Address</p>
                      <p className="text-gray-900 font-medium">{customer.address || customer.postcode || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Billing Information */}
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Billing Information</h2>
                    <button
                      onClick={() => navigate(`/admin/billing?customerId=${customerId || ''}`)}
                      className="text-teal-600 hover:text-teal-900 font-bold text-sm"
                    >
                      Manage
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-bold text-gray-900">{customer.payment_method || 'No payment method on file'}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-gray-900 bg-white px-3 py-1 rounded border border-gray-200">DEFAULT</span>
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Tax ID / VAT</p>
                      <p className="text-gray-900 font-medium">{customer.vat_number || customer.tax_id || '—'}</p>
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Billing Cycle</p>
                      <p className="text-gray-900 font-medium">{customer.billing_cycle || 'Standard'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Recent Activity & Orders</h2>
                    <button
                      onClick={() => navigate(`/admin/orders-jobs?customerId=${customerId || ''}`)}
                      className="text-teal-600 hover:text-teal-900 font-bold text-sm flex items-center gap-1"
                    >
                      View All Orders
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {overviewOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-teal-600 transition-colors cursor-pointer">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                            📦
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{order.id}</p>
                            <p className="text-xs text-gray-600 uppercase tracking-wide">{order.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{order.amount}</p>
                          <p className={`text-xs font-bold uppercase tracking-wide ${order.statusColor}`}>
                            + {order.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Account Health Sidebar */}
                <div className="space-y-6">
                  {/* Account Health */}
                  <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-6">Account Health</h3>
                    <div className="flex flex-col items-center">
                      <div className="relative w-24 h-24 mb-4">
                        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="45" fill="none" stroke="#f0f0f0" strokeWidth="8" />
                          <circle
                            cx="50"
                            cy="50"
                            r="45"
                            fill="none"
                            stroke="#16a34a"
                            strokeWidth="8"
                            strokeDasharray="254"
                            strokeDashoffset="25.4"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <p className="text-2xl font-bold text-gray-900">{customer.account_health != null ? `${customer.account_health}%` : (customer.is_active !== 0 ? '90%' : '—')}</p>
                        </div>
                      </div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">{customer.account_standing || (customer.is_active !== 0 ? 'Good Standing' : 'Suspended')}</p>
                    </div>
                  </div>

                  {/* Account Manager */}
                  <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Account Manager</h3>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        JR
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Julian Rossi</p>
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Senior Partner</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                {/* Recent Managed Orders */}
                <div className="col-span-2">
                  <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Recent Managed Orders</h2>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-teal-600 uppercase tracking-widest">12 ACTIVE</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">48 TOTAL</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {recentOrders.map((order) => (
                        <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:border-teal-600 hover:bg-teal-50 transition-colors cursor-pointer">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                              <div className="text-2xl mt-1">{order.icon}</div>
                              <div className="flex-1">
                                <p className="font-bold text-gray-900">Order #{order.id}</p>
                                <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Asset: {order.asset}</p>
                                <div className="flex items-center gap-4">
                                  <span className={`text-xs font-bold uppercase tracking-wide ${order.status === 'ACTIVE PROCUREMENT' ? 'text-teal-600 bg-teal-50 px-2 py-1 rounded' : 'text-green-600 bg-green-50 px-2 py-1 rounded'}`}>
                                    ● {order.status}
                                  </span>
                                  <span className="text-xs text-gray-600">Lead: {order.lead}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">{order.amount}</p>
                              <p className="text-xs text-gray-600">{order.date}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 text-center">
                      <button
                        onClick={() => navigate(`/admin/orders-jobs?customerId=${customerId || ''}&include=historical`)}
                        className="text-teal-600 hover:text-teal-900 font-bold text-sm uppercase tracking-widest transition-colors"
                      >
                        View All Orders History
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Sidebar */}
                <div className="space-y-6">
                  {/* Properties Portfolio */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest">Properties Portfolio</h3>
                      <button className="text-gray-400 hover:text-gray-600">
                        <span className="text-lg">⊕</span>
                      </button>
                    </div>
                    <div className="space-y-3">
                      {properties.map((property) => (
                        <div key={property.id} className={`${property.image} rounded-lg p-4 text-white h-24 flex flex-col justify-between cursor-pointer hover:shadow-lg transition-shadow`}>
                          <div>
                            <p className="font-bold text-sm">{property.name}</p>
                          </div>
                          <div>
                            <p className="text-xs opacity-90">{property.type}</p>
                            <p className="font-bold text-sm">{property.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate(`/admin/properties?customerId=${customerId || ''}&action=add`)}
                      className="w-full mt-4 text-teal-600 hover:text-teal-900 font-bold text-xs uppercase tracking-widest transition-colors"
                    >
                      Expand Portfolio
                    </button>
                  </div>

                  {/* Administrative Control */}
                  <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 shadow-sm">
                    <h3 className="text-xs font-bold text-teal-400 uppercase tracking-widest mb-4">Administrative Control</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate(`/admin/provider-documents?customerId=${customerId || ''}`)}
                        className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
                      >
                        <span className="font-bold text-sm">Verify Documentation</span>
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/discounts-credits?tab=credits&customerId=${customerId || ''}`)}
                        className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
                      >
                        <span className="font-bold text-sm">Credit Override</span>
                        <CreditCard className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/users?role=admin&assign=${customerId || ''}`)}
                        className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
                      >
                        <span className="font-bold text-sm">Reassign Advisor</span>
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('FREEZE this customer account? They will lose login access until unfrozen.')) return;
                          try {
                            const res: any = await apiService.request(`/admin/users/${customerId}/deactivate`, 'POST', {
                              reason: 'Frozen via customer detail page',
                            });
                            if (res?.success) alert('Account frozen');
                            else alert('Error: ' + (res?.message || 'Failed to freeze account'));
                          } catch (e: any) {
                            alert('Failed: ' + (e?.message || 'Network error'));
                          }
                        }}
                        className="w-full flex items-center justify-between p-3 bg-red-950 hover:bg-red-900 rounded-lg text-red-400 transition-colors"
                      >
                        <span className="font-bold text-sm">Freeze Account</span>
                        <Lock className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Lifetime</p>
                        <p className="text-2xl font-bold text-gray-900">£1.4M</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Score</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-bold text-gray-900">842</p>
                          <button className="w-8 h-8 bg-teal-600 hover:bg-teal-700 rounded-full flex items-center justify-center text-white">
                            <span>+</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                {/* Audit Log Table */}
                <div className="col-span-2">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="p-8 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-2xl font-bold text-gray-900">Account Activity Log</h2>
                        <div className="flex items-center gap-2">
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 016 0v14a1 1 0 01-6 0V4zM15 4a1 1 0 016 0v14a1 1 0 01-6 0V4z" />
                            </svg>
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">Real-time history of all administrative and system events.</p>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Timestamp</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Actor</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Event Category</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Description</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">IP Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map((log) => (
                            <tr key={log.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <p className="font-bold text-gray-900 text-sm">{log.timestamp}</p>
                                <p className="text-xs text-gray-600">{log.time}</p>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                    {log.actorInitial}
                                  </div>
                                  <div>
                                    <p className="font-bold text-gray-900 text-sm">{log.actor}</p>
                                    <p className="text-xs text-gray-600">{log.actorName}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wide ${log.categoryColor}`}>
                                  {log.category}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-700">{log.description}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-xs text-gray-600 font-mono">{log.ip}</p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-600">Showing 5 of 1240 events</span>
                      <div className="flex items-center gap-3">
                        <button className="px-4 py-2 text-gray-600 hover:text-gray-900 font-bold text-sm transition-colors">
                          Previous
                        </button>
                        <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold text-sm rounded transition-colors">
                          Next Page
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Sidebar */}
                <div className="space-y-6">
                  {/* Platform Actions */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Platform Actions</h3>
                    <div className="space-y-3">
                      <button
                        onClick={async () => {
                          if (!window.confirm('Force a password reset for this customer? They will receive an email link.')) return;
                          try {
                            const res: any = await apiService.request(`/admin/users/${customerId}/force-password-reset`, 'POST', {});
                            if (res?.success) alert('Password reset email sent');
                            else alert('Error: ' + (res?.message || 'Failed to send reset'));
                          } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                        }}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                      >
                        <Lock className="w-5 h-5 text-gray-600" />
                        <span className="font-bold text-gray-900 text-sm">Force Password Reset</span>
                      </button>
                      <button
                        onClick={() => navigate(`/admin/support-disputes?customerId=${customerId || ''}&action=new`)}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                      >
                        <MessageCircle className="w-5 h-5 text-gray-600" />
                        <span className="font-bold text-gray-900 text-sm">Send Direct Message</span>
                      </button>
                      <button
                        onClick={() => navigate(`/admin/billing?customerId=${customerId || ''}&action=payment-terms`)}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                      >
                        <CreditCard className="w-5 h-5 text-gray-600" />
                        <span className="font-bold text-gray-900 text-sm">Update Payment Terms</span>
                      </button>
                      <button
                        onClick={() => navigate(`/admin/review-queue?customerId=${customerId || ''}`)}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                      >
                        <FileCheck className="w-5 h-5 text-gray-600" />
                        <span className="font-bold text-gray-900 text-sm">Compliance Audit</span>
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('SUSPEND this customer account?')) return;
                          try {
                            const res: any = await apiService.request(`/admin/users/${customerId}/deactivate`, 'POST', { reason: 'Admin suspension from customer detail' });
                            if (res?.success) alert('Account suspended');
                            else alert('Error: ' + (res?.message || 'Failed to suspend'));
                          } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                        }}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-red-50 rounded-lg transition-colors border border-red-300"
                      >
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="font-bold text-red-600 text-sm uppercase tracking-widest">Suspend Account</span>
                      </button>
                    </div>
                  </div>

                  {/* Quick Observation */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Quick Observation</h3>
                    <p className="text-sm text-gray-700 italic mb-4">
                      "Customer has been increasing order frequency and approvals by 20% month-over-month. Consider account manager outreach for enterprise upgrade."
                    </p>
                    <button
                      onClick={() => setActiveTab('notes')}
                      className="text-teal-600 hover:text-teal-900 font-bold text-xs uppercase tracking-widest transition-colors"
                    >
                      Add Personal Note
                    </button>
                  </div>

                  {/* Account Metrics */}
                  <div className="space-y-4">
                    {/* Security Level */}
                    <div className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5" />
                        <p className="text-xs font-bold uppercase tracking-widest">Security Level</p>
                      </div>
                      <p className="text-2xl font-bold mb-2">Standard</p>
                      <p className="text-sm opacity-90">No active flags or warnings found.</p>
                    </div>

                    {/* Last Login */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Last Login</p>
                      <p className="text-2xl font-bold text-gray-900 mb-1">Today, 18:50</p>
                      <p className="text-sm text-gray-600">From London, GB (Chrome 124)</p>
                    </div>

                    {/* Support Intensity */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Support Intensity</p>
                      <p className="text-2xl font-bold text-gray-900 mb-1">Low</p>
                      <p className="text-sm text-gray-600">2 tickets in last 6 months.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-200">
                      <h2 className="text-2xl font-bold text-gray-900">Quotes</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Quote #</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Service Type</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Property</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Quote Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotes.map((quote, idx) => (
                            <tr key={quote.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <p className="font-bold text-gray-900">{quote.id}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-700">{quote.serviceType}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-700">{quote.property}</p>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${quote.statusColor}`}>
                                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${quote.statusDot}`}></span>
                                  {quote.status}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <p className="font-bold text-gray-900">{quote.amount}</p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-600">Showing 5 of 12 quotes</span>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm transition-colors">Previous</button>
                        <button className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm transition-colors">Next</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Account Health</h3>
                    <p className="text-2xl font-bold text-gray-900 mb-1">Good Standing</p>
                    <p className="text-sm text-gray-600">All quotes active and current.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Properties Tab */}
          {activeTab === 'properties' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2">
                  <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-8">
                      <h2 className="text-2xl font-bold text-gray-900">Properties</h2>
                      <Button className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-4 py-2">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Property
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-6">
                      {properties.map((property, idx) => (
                        <div key={property.id} className={`${property.image} rounded-xl p-6 text-white flex flex-col justify-between min-h-40 cursor-pointer hover:shadow-lg transition-shadow`}>
                          <div>
                            <p className="font-bold text-lg">{property.name}</p>
                          </div>
                          <div>
                            <p className="text-sm opacity-90">{property.type}</p>
                            <p className="font-bold">{property.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Portfolio Summary</p>
                    <p className="text-3xl font-bold text-gray-900 mb-2">£4.8M</p>
                    <p className="text-sm text-gray-600">Across 2 properties</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-200">
                      <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Invoice #</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Issued Date</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Property</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Amount</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr key={invoice.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <p className="font-bold text-gray-900">{invoice.id}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-700">{invoice.issuedDate}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-700">{invoice.property}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="font-bold text-gray-900">{invoice.amount}</p>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${invoice.statusColor}`}>
                                  {invoice.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-600">Showing 1-10 of 42 invoices</span>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm transition-colors">Prev</button>
                        <button className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm transition-colors">Next</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Total Outstanding</p>
                    <p className="text-3xl font-bold text-red-600 mb-2">£1.00</p>
                    <p className="text-xs text-red-600">3 Overdue Invoices</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Docs and Notes Tab */}
          {activeTab === 'docs' && (
            <>
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-2 space-y-8">
                  {/* Documents Section */}
                  <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
                      <label className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs px-4 py-2 rounded-md cursor-pointer">
                        ↓ UPLOAD NEW
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleUploadDocument(f);
                          }}
                        />
                      </label>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Document Name</th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Date Uploaded</th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map((doc) => (
                            <tr key={doc.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{doc.icon}</span>
                                  <p className="font-bold text-gray-900 text-sm">{doc.name}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-700">{doc.type}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-700">{doc.dateUploaded}</p>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-3 py-1 rounded text-xs font-bold ${doc.statusColor}`}>
                                  {doc.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Internal Admin Notes Section */}
                  <div className="bg-gray-100 rounded-xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">Internal Admin Notes</h2>
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">RESTRICTED ACCESS</span>
                    </div>
                    <div className="space-y-4 mb-6">
                      {internalNotes.map((note) => (
                        <div key={note.id} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 ${note.avatarBg} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                                {note.avatar}
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{note.author}</p>
                                <p className="text-xs text-gray-600">{note.timestamp}</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700">{note.content}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <textarea
                        value={internalNoteText}
                        onChange={(e) => setInternalNoteText(e.target.value)}
                        placeholder="Add internal note..."
                        className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 resize-none"
                      ></textarea>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <label
                            className="text-gray-400 hover:text-gray-600 cursor-pointer"
                            title="Attach file"
                          >
                            <span>📎</span>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleUploadDocument(f);
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => setInternalNoteText((t) => t + ' @')}
                            className="text-gray-400 hover:text-gray-600"
                            title="Mention"
                          >
                            <span>@</span>
                          </button>
                        </div>
                        <Button
                          onClick={handleSaveInternalNote}
                          disabled={savingNote}
                          className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-6 py-2 uppercase tracking-widest disabled:opacity-50"
                        >
                          SAVE NOTE
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Sidebar */}
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Documents Summary</p>
                    <p className="text-sm text-gray-700 mb-4">3 documents on file, all verified and current.</p>
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-green-600">✓ ID Verified</p>
                      <p className="text-xs font-bold text-green-600">✓ Compliance Current</p>
                      <p className="text-xs font-bold text-orange-600">⚠ PAT Expiring</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Edit Account Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Edit Account</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">First Name</label>
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Last Name</label>
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Company</label>
                  <input
                    type="text"
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateCustomerProfile(editForm)}
                  disabled={savingCustomer}
                  className="px-4 py-2 bg-teal-600 text-white font-bold text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {savingCustomer ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminCustomerDetailPage;
